import _objectSpread from "@babel/runtime/helpers/objectSpread2";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import Hls from 'hls.js';
import { DEFAULT_HLS_CONFIG } from "../../constants";
import { usePlaybackContext } from "../Playback";
import { HlsContext } from "./HlsContext";
import hlsReducer, { HLS_REDUCER_INITIAL_STATE } from "./hlsReducer";
import useHlsEvents from "./useHlsEvents";
import { jsx as _jsx } from "react/jsx-runtime";
var HlsProvider = function HlsProvider(_ref) {
  var children = _ref.children,
    startPosition = _ref.startPosition,
    hlsConfig = _ref.hlsConfig,
    onErrorProp = _ref.onError,
    ignoreNetworkErrors = _ref.ignoreNetworkErrors,
    defaultAudioLang = _ref.defaultAudioLang,
    onAudioChange = _ref.onAudioChange,
    defaultQuality = _ref.defaultQuality,
    onQualityChange = _ref.onQualityChange,
    onQualityManualChange = _ref.onQualityManualChange,
    withLowQualityWarning = _ref.withLowQualityWarning,
    withManualHighQualityWarning = _ref.withManualHighQualityWarning;
  var _usePlaybackContext = usePlaybackContext(),
    videoRef = _usePlaybackContext.videoRef,
    currentSource = _usePlaybackContext.currentSource,
    currentTime = _usePlaybackContext.currentTime,
    currentProgress = _usePlaybackContext.currentProgress;
  var _useReducer = useReducer(hlsReducer, HLS_REDUCER_INITIAL_STATE),
    _useReducer2 = _slicedToArray(_useReducer, 2),
    _useReducer2$ = _useReducer2[0],
    retriedOnError = _useReducer2$.retriedOnError,
    playerError = _useReducer2$.playerError,
    audios = _useReducer2$.audios,
    currentAudio = _useReducer2$.currentAudio,
    loadingAudio = _useReducer2$.loadingAudio,
    qualities = _useReducer2$.qualities,
    currentQuality = _useReducer2$.currentQuality,
    realCurrentQualityValue = _useReducer2$.realCurrentQualityValue,
    hasLowQuality = _useReducer2$.hasLowQuality,
    currentQualityWarning = _useReducer2$.currentQualityWarning,
    dispatch = _useReducer2[1];

  // Hls instance
  var _useState = useState(),
    _useState2 = _slicedToArray(_useState, 2),
    hls = _useState2[0],
    setHls = _useState2[1];
  var onError = useCallback(function (data) {
    dispatch({
      type: 'SET_ERROR_STATE'
    });
    if (onErrorProp) {
      onErrorProp(data);
    }
  },
  // Ignoring the onError prop, assuming it doesn't change after the first render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  []);
  var resetErrorState = useCallback(function () {
    dispatch({
      type: 'RESET_ERROR_STATE'
    });
  }, []);
  useHlsEvents({
    hls: hls,
    dispatch: dispatch,
    defaultAudioLang: defaultAudioLang,
    onAudioChange: onAudioChange,
    defaultQuality: defaultQuality,
    onQualityChange: onQualityChange,
    withLowQualityWarning: withLowQualityWarning,
    retriedOnError: retriedOnError,
    audios: audios,
    currentQuality: currentQuality,
    currentQualityWarning: currentQualityWarning,
    onError: onError,
    ignoreNetworkErrors: ignoreNetworkErrors,
    resetErrorState: resetErrorState
  });
  useEffect(function () {
    dispatch({
      type: 'RESET_STATE'
    });

    // Destroy current hls instance if exists
    if (hls) {
      hls.detachMedia();
    }
    if (!(currentSource !== null && currentSource !== void 0 && currentSource.isHls)) {
      return;
    }
    var config = _objectSpread(_objectSpread({}, DEFAULT_HLS_CONFIG), {}, {
      startPosition: startPosition
    }, hlsConfig);
    setHls(new Hls(config));
    return function () {
      if (hls) {
        hls.detachMedia();
      }
    };
    // We just want this to run when the source changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSource === null || currentSource === void 0 ? void 0 : currentSource.isHls, currentSource === null || currentSource === void 0 ? void 0 : currentSource.src]);
  useEffect(function () {
    if (!hls || !(currentSource !== null && currentSource !== void 0 && currentSource.isHls) || !videoRef.current) {
      return;
    }

    // Load current source
    hls.loadSource(currentSource.src);

    // bind hls with video element
    hls.attachMedia(videoRef.current);
    // We just want this to run when a new instance was created.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hls]);
  var changeAudio = useCallback(function (audio) {
    if (hls) {
      hls.audioTrack = audio.value;
      dispatch({
        type: 'CHANGE_AUDIO_STATE',
        currentAudio: audio
      });
    }
  }, [hls]);
  var changeQuality = useCallback(function (quality) {
    if (hls) {
      hls.currentLevel = quality.value;
      dispatch({
        type: 'CHANGE_QUALITY_STATE',
        currentQuality: quality
      });
      var selectedQualityValue = quality.value === -1 ? 'auto' : Number(quality.label.split(' ')[0].replace('p', ''));

      // Validate if a warning should be shown
      if (withManualHighQualityWarning && typeof selectedQualityValue === 'number') {
        var minimumHighQuality = withManualHighQualityWarning.minimumHighQuality,
          onWarningShown = withManualHighQualityWarning.onWarningShown;
        if (currentQualityWarning !== 'manual-high-quality' && selectedQualityValue >= minimumHighQuality) {
          dispatch({
            type: 'CHANGE_QUALITY_STATE',
            currentQualityWarning: 'manual-high-quality'
          });
          if (onWarningShown) {
            onWarningShown({
              currentTime: currentTime,
              currentProgress: currentProgress,
              quality: selectedQualityValue
            });
          }
        }
      }
      // Validate callback
      if (onQualityManualChange) {
        onQualityManualChange(selectedQualityValue.toString());
      }
    }
  },
  // Ignoring onQualityManualChange, assuming it doesn't change after the first render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [currentProgress, currentTime, currentQualityWarning, withManualHighQualityWarning]);
  var hideQualityWarning = useCallback(function () {
    dispatch({
      type: 'HIDE_QUALITY_WARNING'
    });
  }, []);
  var currentQualityLabel = useMemo(function () {
    if (!qualities || !currentQuality) {
      return undefined;
    }
    return currentQuality.value === -1 ? 'AUTO' : currentQuality.label.split(' ')[0];
    // Ignoring qualities array because we just need to now if it exists.
    // Ignoring currentQuality object because we just need to check if the value changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!qualities, currentQuality === null || currentQuality === void 0 ? void 0 : currentQuality.value]);
  var contextValue = useMemo(function () {
    return {
      playerError: playerError,
      onError: onError,
      resetErrorState: resetErrorState,
      audios: audios,
      currentAudio: currentAudio,
      loadingAudio: loadingAudio,
      changeAudio: changeAudio,
      qualities: qualities,
      currentQuality: currentQuality,
      currentQualityLabel: currentQualityLabel,
      realCurrentQualityValue: realCurrentQualityValue,
      hasLowQuality: hasLowQuality,
      currentQualityWarning: currentQualityWarning,
      changeQuality: changeQuality,
      hideQualityWarning: hideQualityWarning,
      withLowQualityWarning: withLowQualityWarning,
      withManualHighQualityWarning: withManualHighQualityWarning
    };
  },
  // Ignoring withLowQualityWarning object because we're only including the dynamic properties.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [playerError, onError, resetErrorState, audios, currentAudio, loadingAudio, changeAudio, qualities, currentQuality, currentQualityLabel, realCurrentQualityValue, hasLowQuality, currentQualityWarning, changeQuality, hideQualityWarning, withLowQualityWarning === null || withLowQualityWarning === void 0 ? void 0 : withLowQualityWarning.minimumVideoProgress, withLowQualityWarning === null || withLowQualityWarning === void 0 ? void 0 : withLowQualityWarning.maximumLowQuality, withLowQualityWarning === null || withLowQualityWarning === void 0 ? void 0 : withLowQualityWarning.dismissBannerAfterSeconds, withLowQualityWarning === null || withLowQualityWarning === void 0 ? void 0 : withLowQualityWarning.dismissPopperAfterSeconds, withManualHighQualityWarning === null || withManualHighQualityWarning === void 0 ? void 0 : withManualHighQualityWarning.minimumHighQuality, withManualHighQualityWarning === null || withManualHighQualityWarning === void 0 ? void 0 : withManualHighQualityWarning.dismissBannerAfterSeconds, withManualHighQualityWarning === null || withManualHighQualityWarning === void 0 ? void 0 : withManualHighQualityWarning.dismissPopperAfterSeconds]);
  return /*#__PURE__*/_jsx(HlsContext.Provider, {
    value: contextValue,
    children: children
  });
};
export default HlsProvider;